import React, { useCallback, useEffect, useState } from 'react';
import { fetchDevices, fetchNetworkTopology, NetworkTopology as INetworkTopology, TopologyLink, ZigbeeNetwork } from '../services/api';
import { Device } from '../types';

// ── helpers ────────────────────────────────────────────────────────────────

function lqiColor(lqi: number): string {
    if (lqi >= 180) return '#22c55e'; // green
    if (lqi >= 100) return '#f59e0b'; // amber
    return '#ef4444';                  // red
}

function lqiLabel(lqi: number): string {
    if (lqi >= 180) return 'bra';
    if (lqi >= 100) return 'sådär';
    return 'svag';
}

/** Build parent→children adjacency from the link list.
 *  When a device appears as a child of multiple parents (e.g. coordinator neighbor table
 *  has a phantom LQI=0 entry AND a real router has it as a child with LQI=165),
 *  we keep only the parent with the highest LQI — that reflects the actual RF path.
 *
 *  Orphan adoption: if a router has outgoing links (children) but no parent link in the
 *  scan data, it is assumed to be a direct child of the coordinator. This handles the common
 *  case where a router's uplink entry was missing from the coordinator's neighbor table and
 *  the router itself didn't report the coordinator as relationship=0 in its own LQI table. */
function buildTree(links: TopologyLink[], coordinatorIeee: string | null) {
    // Pass 1: for each target device, pick the single best-LQI parent link
    const bestLink = new Map<string, TopologyLink>(); // target → best parent link
    for (const link of links) {
        const existing = bestLink.get(link.target);
        if (!existing || link.lqi > existing.lqi) {
            bestLink.set(link.target, link);
        }
    }

    // Pass 2: build children map from winning links only
    const children = new Map<string, Array<{ ieee: string; lqi: number | null }>>();
    const addChild = (parent: string, child: string, lqi: number | null) => {
        const kids = children.get(parent) ?? [];
        kids.push({ ieee: child, lqi });
        children.set(parent, kids);
    };
    for (const [child, link] of bestLink) {
        addChild(link.source, child, link.lqi);
    }

    // Pass 3: orphan adoption — routers that have children but no parent link must
    // be direct coordinator children whose uplink wasn't captured in the scan.
    if (coordinatorIeee) {
        const hasParent = new Set<string>(bestLink.keys());
        const adopted = new Set<string>();
        for (const link of links) {
            const source = link.source;
            if (source !== coordinatorIeee && !hasParent.has(source) && !adopted.has(source)) {
                addChild(coordinatorIeee, source, null); // LQI not captured
                adopted.add(source);
            }
        }
    }

    return { children, root: coordinatorIeee };
}

/** Reconstruct path from a node to root (coordinator) */
function buildPaths(
    children: Map<string, Array<{ ieee: string; lqi: number | null }>>,
    root: string,
): Map<string, Array<{ ieee: string; lqi: number | null }>> {
    // path for a node = [root, …intermediaries, node]
    const result = new Map<string, Array<{ ieee: string; lqi: number | null }>>();

    function dfs(nodeIeee: string, pathSoFar: Array<{ ieee: string; lqi: number | null }>) {
        result.set(nodeIeee, pathSoFar);
        const kids = children.get(nodeIeee) ?? [];
        for (const child of kids) {
            dfs(child.ieee, [...pathSoFar, { ieee: child.ieee, lqi: child.lqi }]);
        }
    }

    dfs(root, [{ ieee: root, lqi: null }]);
    return result;
}

// ── component ──────────────────────────────────────────────────────────────

const NetworkTopology: React.FC = () => {
    const [topology, setTopology] = useState<INetworkTopology | null>(null);
    const [devices, setDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const deviceName = useCallback(
        (ieee: string) => {
            const d = devices.find(x => x.ieee_address === ieee);
            return d?.name ?? ieee;
        },
        [devices],
    );

    const isKnownDevice = useCallback(
        (ieee: string) => devices.some(x => x.ieee_address === ieee),
        [devices],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [topo, devs] = await Promise.all([fetchNetworkTopology(), fetchDevices()]);
            setTopology(topo);
            setDevices(devs);
            setLastFetched(new Date());
        } catch (e: any) {
            setError(e?.message ?? 'Kunde inte hämta nätverkskartan');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDevices().then(setDevices).catch(() => {}); }, []);

    // ── derived tree, one per coordinator ─────────────────────────────────
    type Row = { ieee: string; path: Array<{ ieee: string; lqi: number | null }> | null };
    const networks: Array<{ net: ZigbeeNetwork; rows: Row[] }> = (topology?.networks ?? []).map(net => {
        if (!net.coordinatorIeee) return { net, rows: [] };
        const { children, root } = buildTree(net.links, net.coordinatorIeee);
        const paths = buildPaths(children, root!);

        // All non-coordinator nodes
        const allDeviceIeees = new Set<string>();
        for (const link of net.links) {
            allDeviceIeees.add(link.source);
            allDeviceIeees.add(link.target);
        }

        // Sort: routers first (shorter path), then end devices; unreachable at end
        const rows = Array.from(allDeviceIeees)
            .filter(ieee => ieee !== root)
            .map(ieee => ({ ieee, path: paths.get(ieee) ?? null }))
            .sort((a, b) => {
                const la = a.path?.length ?? 999;
                const lb = b.path?.length ?? 999;
                return la - lb || a.ieee.localeCompare(b.ieee);
            });
        return { net, rows };
    });
    const totalRows = networks.reduce((n, x) => n + x.rows.length, 0);

    // ── styles ────────────────────────────────────────────────────────────
    const card: React.CSSProperties = {
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,.1)',
        padding: '20px 24px',
        marginBottom: 20,
    };

    const badge = (lqi: number): React.CSSProperties => ({
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        color: '#fff',
        background: lqiColor(lqi),
        marginLeft: 6,
        whiteSpace: 'nowrap',
    });

    const pathStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        fontSize: 13,
    };

    const nodeStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#f0f2f5',
        borderRadius: 6,
        padding: '3px 10px',
        fontWeight: 500,
        fontSize: 13,
        color: '#1e293b',
    };

    const coordStyle: React.CSSProperties = {
        ...nodeStyle,
        background: '#dbeafe',
        color: '#1d4ed8',
    };

    const routerStyle: React.CSSProperties = {
        ...nodeStyle,
        background: '#f3e8ff',
        color: '#7c3aed',
    };

    const arrow: React.CSSProperties = {
        color: '#94a3b8',
        fontSize: 16,
        lineHeight: 1,
        userSelect: 'none',
    };

    return (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
            {/* Header */}
            <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1e293b' }}>
                        📡 Zigbee-nätverket
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
                        Visar vägen från varje enhet upp till samordnaren.
                        {lastFetched && (
                            <> Senast avläst: {lastFetched.toLocaleTimeString('sv-SE')}.</>
                        )}
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    style={{
                        padding: '8px 18px',
                        borderRadius: 8,
                        border: 'none',
                        background: loading ? '#94a3b8' : '#3b82f6',
                        color: '#fff',
                        fontWeight: 600,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {loading ? 'Läser av…' : '🔄 Läs av nätverket'}
                </button>
            </div>

            {/* Debug: raw link data */}
            {topology && (
                <details style={{ ...card, marginBottom: 8 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                        🔍 Felsökning: rådata ({topology.networks.length} samordnare)
                    </summary>
                    <pre style={{ fontSize: 11, marginTop: 8, overflowX: 'auto', maxHeight: 300, background: '#f8fafc', padding: 8, borderRadius: 6 }}>
                        {JSON.stringify(topology.networks, null, 2)}
                    </pre>
                </details>
            )}

            {/* Legend */}
            <div style={{ ...card, display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 14, paddingBottom: 14 }}>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Teckenförklaring:</span>
                <span style={coordStyle}>Samordnare</span>
                <span style={routerStyle}>Router / repeater</span>
                <span style={nodeStyle}>Slutenhet</span>
                <span style={{ fontSize: 13 }}>
                    Signal: <span style={badge(200)}>bra ≥ 180</span>
                    <span style={badge(130)}>sådär ≥ 100</span>
                    <span style={badge(50)}>svag &lt; 100</span>
                </span>
            </div>

            {/* Error */}
            {error && (
                <div style={{ ...card, background: '#fef2f2', color: '#dc2626', fontWeight: 500 }}>
                    ⚠️ {error}
                </div>
            )}

            {/* Loading skeleton */}
            {loading && !topology && (
                <div style={{ ...card, textAlign: 'center', color: '#64748b', padding: 40 }}>
                    Läser av signaltabeller från samordnaren och routrarna… det kan ta 10–30 sekunder.
                </div>
            )}

            {/* No data */}
            {!loading && topology && totalRows === 0 && networks.every(x => !x.net.error) && (
                <div style={{ ...card, textAlign: 'center', color: '#64748b', padding: 40 }}>
                    Det finns ingen nätverksdata. Kontrollera att Zigbee-samordnaren är ansluten.
                </div>
            )}

            {/* One section per coordinator — each has its own network */}
            {!loading && networks.map(({ net, rows }) => (
                <div key={net.gatewayId} style={{ marginBottom: 24 }}>
                    {/* Coordinator row */}
                    <div style={{ ...card, marginBottom: 8, paddingTop: 14, paddingBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 18 }}>🏠</span>
                            <span style={coordStyle}>{net.gatewayName}</span>
                            {net.coordinatorIeee && (
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>{net.coordinatorIeee}</span>
                            )}
                            <span style={{ color: '#64748b', fontSize: 12 }}>
                                {rows.length} {rows.length === 1 ? 'enhet' : 'enheter'}
                            </span>
                        </div>
                        {net.error && (
                            <div style={{ marginTop: 6, color: '#dc2626', fontSize: 12 }}>
                                ⚠️ Kunde inte läsa av den här samordnaren: {net.error}
                            </div>
                        )}
                        {!net.error && !net.coordinatorIeee && (
                            <div style={{ marginTop: 6, color: '#64748b', fontSize: 12 }}>
                                Samordnaren är inte ansluten.
                            </div>
                        )}
                    </div>

                    {/* Device rows */}
                    {rows.map(({ ieee, path }) => {
                        const name = deviceName(ieee);
                        const displayName = name !== ieee ? name : null;

                        const nodeLabel = (nodeIeee: string) => {
                            if (!isKnownDevice(nodeIeee)) {
                                // This IEEE exists in topology links but isn't in the device list —
                                // likely a Zigbee router (plug, bulb, etc.) not registered in the system.
                                return <><span style={{ opacity: 0.7 }}>🔌 Okänd router</span><span style={{ fontWeight: 400, opacity: 0.5, marginLeft: 4, fontSize: 11, fontFamily: 'monospace' }}>{nodeIeee.slice(-6)}</span></>;
                            }
                            const n = deviceName(nodeIeee);
                            return n !== nodeIeee
                                ? <><strong>{n}</strong><span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 4, fontSize: 11 }}>{nodeIeee.slice(-6)}</span></>
                                : <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{nodeIeee.slice(-8)}</span>;
                        };

                        // Device appeared in topology links but path to coordinator couldn't be built
                        // (e.g. its parent router timed out during the scan)
                        if (!path) {
                            return (
                                <div key={ieee} style={{ ...card, marginBottom: 8, paddingTop: 14, paddingBottom: 14, opacity: 0.65 }}>
                                    <div style={{ ...pathStyle, color: '#94a3b8' }}>
                                        <span style={nodeStyle}>{nodeLabel(ieee)}</span>
                                        <span style={{ fontSize: 12, marginLeft: 8 }}>— vägen kunde inte läsas av</span>
                                    </div>
                                    <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 11 }}>
                                        {displayName && <><strong style={{ color: '#475569' }}>{displayName}</strong>{' · '}</>}
                                        <span style={{ fontFamily: 'monospace' }}>{ieee}</span>
                                        {' · '}
                                        <span style={{ color: '#f59e0b' }}>⚠ routern ovanför svarade inte — prova att läsa av igen</span>
                                    </div>
                                </div>
                            );
                        }

                        // path[0] = coordinator, path[last] = this device
                        const lqiToParent = path[path.length - 1]?.lqi ?? null;

                        return (
                            <div key={ieee} style={{ ...card, marginBottom: 8, paddingTop: 14, paddingBottom: 14 }}>
                                <div style={pathStyle}>
                                    {path.map((node, idx) => {
                                        const isCoord = idx === 0;
                                        const isThis = idx === path.length - 1;
                                        const unknown = !isCoord && !isKnownDevice(node.ieee);
                                        const style = isCoord
                                            ? coordStyle
                                            : unknown
                                                ? { ...routerStyle, opacity: 0.75, border: '1px dashed #c4b5fd' }
                                                : isThis
                                                    ? nodeStyle
                                                    : routerStyle;

                                        return (
                                            <React.Fragment key={node.ieee}>
                                                {idx > 0 && (
                                                    <span style={arrow}>›</span>
                                                )}
                                                <span style={style}>
                                                    {isCoord ? net.gatewayName : nodeLabel(node.ieee)}
                                                    {idx > 0 && node.lqi !== null && (
                                                        <span style={badge(node.lqi)}>
                                                            {node.lqi} · {lqiLabel(node.lqi)}
                                                        </span>
                                                    )}
                                                    {idx > 0 && node.lqi === null && (
                                                        <span style={{ ...badge(0), background: '#94a3b8' }}>? · okänd</span>
                                                    )}
                                                </span>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                                <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 11 }}>
                                    {displayName && <><strong style={{ color: '#475569' }}>{displayName}</strong>{' · '}</>}
                                    <span style={{ fontFamily: 'monospace' }}>{ieee}</span>
                                    {' · '}
                                    {path.length - 1} {path.length - 1 === 1 ? 'steg' : 'steg'}
                                    {' · '}
                                    Signal till närmaste nod: {lqiToParent !== null
                                        ? <strong style={{ color: lqiColor(lqiToParent) }}>{lqiToParent}</strong>
                                        : <strong style={{ color: '#94a3b8' }}>okänd</strong>}
                                    {lqiToParent === 0 && path.length === 2 && (
                                        <span style={{ marginLeft: 6, color: '#f59e0b' }}
                                              title="Signal 0 betyder att samordnarens grannlista har en gammal spökpost — den riktiga vägen går troligen via en router">
                                            ⚠ spökpost, verklig väg okänd
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default NetworkTopology;
